import * as React from 'react';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../store';
import { Dispatch } from 'redux';
import 'hammerjs';
// Components
import { GetString } from '../StringResourceManager';
import { AssetStateCountResponse } from '../../Interfaces/Assets/AssetStateCountResponse';
import { getAssetStateNameCount } from '../../store/Devices/AssetStatesActions';
import OverviewWidgetChart from '../OverviewWidgetChart';
import { ChartSeriesInterface } from '../../Interfaces/Devices/ChartSeriesInterface';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { SeriesClickEvent } from '@progress/kendo-react-charts';
import { AssetInfo } from '../../Interfaces/Assets/AssetInfo';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { saveAssetStatusesFilters } from '../../store/UserSettings/UserSettingsActions';
import { ASSET } from '../../utils/constants';
import { ChartColors } from './ChartColors';

export default function DeviceIssuesByCategoryChart() {
  const [refresh, setRefresh] = useState<boolean>(true);

  const assetStatusCounts: Map<string, AssetStateCountResponse[]> = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.assetStatusCounts
  );

  const isFetchingAssetStatusCounts: boolean = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.isFetchingAssetStatusCounts
  );

  const [deviceWithIssuesList, setDeviceWithIssuesList] = React.useState<
    string[]
  >([]);

  const [
    deviceIssuesByCategorySeriesData,
    setDeviceIssuesByCategorySeriesData,
  ] = React.useState<ChartSeriesInterface[]>([]);

  const [errorString, setErrorString] = React.useState<string>(
    GetString('ERROR')
  );
  const [warningString, setWarningString] = React.useState<string>(
    GetString('WARNING')
  );

  const [statusCounts, setStatusCounts] = React.useState<
    Map<string, AssetStateCountResponse[]>
  >(new Map());

  const dispatch: Dispatch<any> = useDispatch();

  useEffect((): ReturnType<React.EffectCallback> => {
    if (refresh) {
      setRefresh(false);
    }
    return (): void => {};
  }, [refresh]);

  useEffect((): ReturnType<React.EffectCallback> => {
    validateAssetStatusCounts(assetStatusCounts);
  }, [assetStatusCounts]);

  useEffect((): ReturnType<React.EffectCallback> => {
    processChartData();
  }, [statusCounts]);

  const validateAssetStatusCounts = (
    count: Map<string, AssetStateCountResponse[]>
  ) => {
    if (count.size == 0) return;

    Object.entries(count).map(([key, value], index) => {
      var assetStateCountResponse: AssetStateCountResponse[] = value;
      if (
        key !== 'U-Scan' &&
        assetStateCountResponse[0].Name !== 'NodeAvailabilityStatus'
      )
        setStatusCounts(count);
      return;
    });
  };
  const processChartData = () => {
    let list: string[] = [];
    let series: ChartSeriesInterface[] = [];

    list = getDeviceWithIssueList();

    series = setDeviceIssuesCount(list);

    setDeviceIssuesByCategorySeriesData(series);
  };

  const getDeviceWithIssueList = () => {
    let deviceWithIssueList: string[] = [];

    Object.entries(statusCounts).map(([key, value], index) => {
      var assetStateCountResponse: AssetStateCountResponse[] = value;
      var issueFound: boolean = false;
      for (let i = 0; i < assetStateCountResponse.length; ++i) {
        var seriesIndex = Number(assetStateCountResponse[i].Value);

        if (seriesIndex == 2 || seriesIndex == 3) {
          issueFound = true;
          break;
        }
      }

      if (issueFound) deviceWithIssueList.push(key);

      setDeviceWithIssuesList(deviceWithIssueList);
    });

    return deviceWithIssueList;
  };

  const setDeviceIssuesCount = (deviceWithIssueList: string[]) => {
    let series: ChartSeriesInterface[] =
      initialDeviceIssuesByCategorySeriesInterface;

    for (var i = 0; i < series.length; i++) {
      for (var li = 0; li < deviceWithIssueList.length; li++)
        series[i].count.push(0);
    }

    Object.entries(statusCounts).map(([key, value]) => {
      if (deviceWithIssueList.includes(key)) {
        var assetStateCountResponse: AssetStateCountResponse[] = value;

        for (let i = 0; i < assetStateCountResponse.length; ++i) {
          var seriesIndex = Number(assetStateCountResponse[i].Value);

          if (seriesIndex == 0 || seriesIndex == 1) {
            continue;
          }

          if (seriesIndex == 2) {
            seriesIndex = 0;
          } else if (seriesIndex == 3) {
            seriesIndex = 1;
          }

          let index = deviceWithIssueList.findIndex(
            (deviceName: string) => deviceName === key
          );

          series[seriesIndex].count[index] = Number(
            assetStateCountResponse[i].Total
          );
        }
      }
    });

    return series;
  };

  const initialDeviceIssuesByCategorySeriesInterface: ChartSeriesInterface[] = [
    {
      status: GetString('WARNING'),
      count: [],
      color: ChartColors.Orange,
    },
    {
      status: GetString('ERROR'),
      count: [],
      color: ChartColors.Red,
    },
  ];

  const maxChartValueAxisItem: any = () => {
    return deviceIssuesByCategorySeriesData.length > 0
      ? Math.max(
          ...deviceIssuesByCategorySeriesData[0].count,
          ...deviceIssuesByCategorySeriesData[1].count
        ) + 10
      : 0;
  };

  const onDeviceCategorySeriesItemClick = (event: SeriesClickEvent) => {
    const series: any = event.series;

    const dataItem: AssetInfo = {
      Category: event.category,
      Name: ASSET.FIELD_STATUS_LABEL,
      Value:
        series.name === errorString
          ? ApiEnums.DeviceStatus.Error.toString()
          : series.name === warningString
          ? ApiEnums.DeviceStatus.Inconvenienced.toString()
          : '',
    };

    let filter: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [
        {
          field: ASSET.CURRENT_ISSUE,
          operator: 'eq',
          value: dataItem,
        },
      ],
    };

    saveAssetStatusesFilters(dispatch, filter);
  };

  return (
    <OverviewWidgetChart
      chartTitleText={GetString('DEVICE_ISSUES_BY_CATEGORY')}
      chartValueAxisItemtitle={GetString('NUMBER_OF_ISSUES')}
      chartCategoryAxisItem={deviceWithIssuesList}
      maxChartValueAxisItem={maxChartValueAxisItem()}
      seriesData={deviceIssuesByCategorySeriesData}
      chartSeriesClick={onDeviceCategorySeriesItemClick}
      chartType="bar"
      goToScreen="devices"
      isRefreshing={isFetchingAssetStatusCounts}
    />
  );
}
